import type { BoardPlacement } from "./Board.types";
import { getCellKey, type BoardCells } from "./boardCells";
import { stepThrough } from "./boardCollapse";
import {
    getEntriesWithout,
    locateBoardEntry,
    type BoardDropTarget,
} from "./boardMove";

export type BoardKeyboardDirection = "up" | "down" | "left" | "right";

/** Arrow keys by name; an arrow's `key` and `code` are the same. */
export const BOARD_ARROW_DIRECTIONS: Partial<
    Record<string, BoardKeyboardDirection>
> = {
    ArrowUp: "up",
    ArrowDown: "down",
    ArrowLeft: "left",
    ArrowRight: "right",
};

interface CardDropTargetArgs<TItem> {
    cells: BoardCells<TItem>;
    activePlacementKey: string;
    overPlacementKey: string;
    /** The pointer is in the hovered card's lower half. */
    isAfter: boolean;
}

/** Hovering a card lands before it, or after it from its lower half. */
export const getCardDropTarget = <TItem>({
    cells,
    activePlacementKey,
    overPlacementKey,
    isAfter,
}: CardDropTargetArgs<TItem>): BoardDropTarget | null => {
    const over = locateBoardEntry(cells, overPlacementKey);
    if (over === null) {
        return null;
    }
    const { placement } = over.entry;
    const index = getEntriesWithout(
        cells,
        placement,
        activePlacementKey,
    ).findIndex((entry) => entry.placementKey === overPlacementKey);
    return { placement, index: index + (isAfter ? 1 : 0) };
};

interface CellDropTargetArgs<TItem> {
    cells: BoardCells<TItem>;
    activePlacementKey: string;
    placement: BoardPlacement;
}

/** Hovering a cell with no other card lands at its end. */
export const getCellDropTarget = <TItem>({
    cells,
    activePlacementKey,
    placement,
}: CellDropTargetArgs<TItem>): BoardDropTarget => ({
    placement,
    index: getEntriesWithout(cells, placement, activePlacementKey).length,
});

export interface KeyboardDropTargetArgs<TItem> {
    cells: BoardCells<TItem>;
    activePlacementKey: string;
    /** Where the card is headed now, which may be a refusing cell. */
    target: BoardDropTarget;
    /** Expanded columns, in order. */
    columnIds: readonly string[];
    /** Expanded lanes, in order; `[null]` without lanes. */
    laneIds: ReadonlyArray<string | null>;
    direction: BoardKeyboardDirection;
    /** Sorted cells, and cells refusing the card, have no slots to step through. */
    hasSlots: boolean;
}

/**
 * One arrow key step from the current target. Up and down move between
 * slots in the cell, then into the lane above (its end) or below (its top).
 * Left and right move to the neighbouring column in the same lane at the
 * same index, clamped to its length. Null at the edge: no column or lane
 * left that way.
 */
export const getKeyboardDropTarget = <TItem>({
    cells,
    activePlacementKey,
    target,
    columnIds,
    laneIds,
    direction,
    hasSlots,
}: KeyboardDropTargetArgs<TItem>): BoardDropTarget | null => {
    const { placement, index } = target;
    const getLength = (cellPlacement: BoardPlacement) =>
        getEntriesWithout(cells, cellPlacement, activePlacementKey).length;

    if (direction === "up" || direction === "down") {
        const step = direction === "down" ? 1 : -1;
        const nextIndex = index + step;
        if (hasSlots && nextIndex >= 0 && nextIndex <= getLength(placement)) {
            return { placement, index: nextIndex };
        }
        const lane = stepThrough({ ids: laneIds, id: placement.laneId, step });
        if (lane === undefined) {
            return null;
        }
        const nextPlacement = { columnId: placement.columnId, laneId: lane.id };
        return {
            placement: nextPlacement,
            index: step > 0 ? 0 : getLength(nextPlacement),
        };
    }

    const column = stepThrough({
        ids: columnIds,
        id: placement.columnId,
        step: direction === "right" ? 1 : -1,
    });
    if (column === undefined) {
        return null;
    }
    const nextPlacement = { columnId: column.id, laneId: placement.laneId };
    return {
        placement: nextPlacement,
        index: Math.min(index, getLength(nextPlacement)),
    };
};

export const isSameDropTarget = (
    first: BoardDropTarget,
    second: BoardDropTarget,
) =>
    first.index === second.index &&
    getCellKey(first.placement) === getCellKey(second.placement);
